<template>
  <div class="loan-card">
    <validation-observer ref="observer">
      <b-form :novalidate="true">
        <b-row>
          <b-col lg="6">
            <forms-validated-input
              name="departure_at"
              :label="$t('loans.fields.departure_at') | capitalize"
              type="datetime"
              :disabled-dates-fct="disabledBeforeToday"
              :disabled-times-fct="disabledBeforeNow"
              :value="departureAt"
              :timezone="loanableTimezone"
              :clearable="false"
              :description="
                zonedTimes.differsFromCurrentTimezone
                  ? $t('components.loan.new_loan_form.timezone.different', {
                      timezone: zonedTimes.timezoneAbbrev,
                    })
                  : null
              "
              @input="changeSearchDate"
            >
              <template #footer>
                <button class="mx-btn mx-btn-text" @click="setDepartureToEarliest">
                  {{ $t("components.loan.new_loan_form.duration.now") }}
                </button>
              </template>
            </forms-validated-input>
          </b-col>

          <b-col lg="6">
            <forms-validated-input
              :value="returnAt"
              name="return_at"
              :label="$t('loans.fields.return_at') | capitalize"
              type="datetime"
              :clearable="false"
              :disabled-dates-fct="disabledBeforeDepartureDay"
              :disabled-times-fct="disabledBeforeDeparture"
              :timezone="loanableTimezone"
              @input="setReturnAt"
            >
              <template #footer>
                <button class="mx-btn mx-btn-text" @click="() => setDuration(60)">
                  {{ $t("components.loan.new_loan_form.duration.one_hour") }}
                </button>
                <button class="mx-btn mx-btn-text" @click="() => setDuration(60 * 3)">
                  {{ $t("components.loan.new_loan_form.duration.three_hours") }}
                </button>
                <button class="mx-btn mx-btn-text" @click="() => setDuration(60 * 24)">
                  {{ $t("components.loan.new_loan_form.duration.one_day") }}
                </button>
              </template>
            </forms-validated-input>
          </b-col>
        </b-row>

        <b-row v-if="invalidDuration">
          <b-col>
            <b-alert show variant="danger">
              {{ $t("components.loan.new_loan_form.duration.invalid") }}
            </b-alert>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <forms-validated-input
              v-model="localEstimatedDistance"
              name="estimated_distance"
              :label="$t('loans.fields.estimated_distance') | capitalize"
              type="number"
              :min="0"
              :rules="{ min_value: 0, required: true }"
              mask="#######"
              :formatter="integer"
              @input="onEstimatedDistanceChange"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" :md="alternativeTo === 'other' ? 6 : 12">
            <forms-validated-input
              v-model="alternativeTo"
              name="alternative_to"
              :label="$t('loans.fields.alternative_to') | capitalize"
              :rules="{
                required: true,
              }"
              type="select"
              :options="alternativeToOptions"
              @input="
                (value) => {
                  if (value !== 'other') {
                    alternativeToOther = null;
                  }
                }
              "
            />
          </b-col>
          <b-col v-if="alternativeTo === 'other'">
            <forms-validated-input
              v-model="alternativeToOther"
              name="alternative_to_other"
              :label="$t('loans.fields.alternative_to_other') | capitalize"
              :rules="{
                required: true,
              }"
              type="text"
            />
          </b-col>
        </b-row>

        <!-- No message for owner if the loanable is self service. -->
        <forms-validated-input
          v-if="!loanWillBeSelfService"
          v-model="messageForOwner"
          name="message_for_owner"
          :label="
            (`${$t('loans.fields.message_for_owner')} ` + `(${$t('forms.optional')})`) | capitalize
          "
          type="markdown"
        />
        <div class="text-center">
          <template v-if="!available">
            <div
              v-if="minDurationInMinutes !== null && durationInMinutes < minDurationInMinutes"
              class="text-muted small mb-2"
            >
              {{
                $t("loanables.loan_duration.min.message", {
                  duration: duration(minDurationInMinutes),
                })
              }}
            </div>
            <div
              v-if="maxDurationInMinutes !== null && durationInMinutes > maxDurationInMinutes"
              class="text-muted small mb-2"
            >
              {{
                $t("loanables.loan_duration.max.message", {
                  duration: duration(maxDurationInMinutes),
                })
              }}
            </div>
            <icon-button v-if="!available" variant="primary" disabled type="submit">
              {{ $t("components.loan.new_loan_form.buttons.unavailable") }}
            </icon-button>
          </template>
          <icon-button
            v-else
            ref="submitButton"
            :onclick="submit"
            variant="success"
            :disabled="estimating || invalidDuration || hasBlockingIncidents"
          >
            {{
              loanWillBeSelfService
                ? $t("components.loan.new_loan_form.buttons.reserve")
                : $t("components.loan.new_loan_form.buttons.send_request")
            }}
          </icon-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { duration, integer } from "@/helpers/filters";
import { getEarliestDeparture } from "@/helpers/loanDepartures";
import { getZonedTimes } from "@/helpers/loanTimezones";
import { post } from "@/requests/server";
import dayjs from "dayjs";
import { flexibleDebounce } from "@/helpers/debounce";

export default {
  name: "NewLoanForm",
  components: {
    IconButton,
    FormsValidatedInput,
  },
  props: {
    available: {
      type: Boolean,
      default: true,
    },
    estimatedDistance: {
      type: Number,
      required: false,
      default: null,
    },
    estimating: {
      type: Boolean,
      default: false,
    },
    hasBlockingIncidents: {
      type: Boolean,
      default: false,
    },
    loan: {
      type: Object,
      required: true,
    },
    loanableId: {
      type: [Number, String],
      required: true,
    },
    loanableTimezone: {
      type: String,
      required: true,
    },
    maxDurationInMinutes: {
      type: Number,
      default: null,
    },
    minDurationInMinutes: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      localEstimatedDistance: this.loan?.estimated_distance ?? this.estimatedDistance,
      messageForOwner: null,
      alternativeTo: null,
      alternativeToOther: null,
      alternativeToOptions: [
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.delivery"),
          value: "delivery",
        },
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.car"),
          value: "car",
        },
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.public_transit"),
          value: "public_transit",
        },
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.bike"),
          value: "bike",
        },
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.walking"),
          value: "walking",
        },
        {
          text: this.$t("components.loan.new_loan_form.alternative_to.other"),
          value: "other",
        },
      ],
    };
  },
  computed: {
    departureAt() {
      return this.loan?.departure_at;
    },
    durationInMinutes() {
      return this.loan.duration_in_minutes;
    },
    returnAt() {
      return dayjs(this.departureAt).add(this.durationInMinutes, "minute").toISOString();
    },
    zonedTimes() {
      return getZonedTimes(this.loan);
    },
    invalidDuration() {
      return this.durationInMinutes < 15;
    },
    loanWillBeSelfService() {
      return (
        this.loan.loanable.sharing_mode === "self_service" ||
        (this.loan.loanable.sharing_mode === "hybrid" &&
          this.$store.state.user?.trusted_for_loanables?.includes(this.loan.loanable.id))
      );
    },
  },
  methods: {
    dayjs,
    duration,
    integer,
    onEstimatedDistanceChange(event) {
      // Save estimated distance to simplify comparing prices between different loanables
      this.$store.commit("loanable.search/estimatedDistance", this.localEstimatedDistance);

      if (!event) {
        return;
      }

      // estimate if we're not clicking on the submit button
      if (
        !event.relatedTarget ||
        !this.$refs.submitButton ||
        event.relatedTarget.closest("button") !== this.$refs.submitButton.$el
      ) {
        this.estimateLoan();
      }
    },
    async submit() {
      await this.$nextTick();

      let isValid = await this.$refs.observer.validate();
      if (isValid) {
        await this.createLoan();
      }
    },
    async createLoan() {
      const { data } = await post(
        "/loans",
        {
          departure_at: this.departureAt,
          duration_in_minutes: this.durationInMinutes,
          estimated_distance: this.localEstimatedDistance,
          message_for_owner: this.messageForOwner,
          alternative_to: this.alternativeTo,
          alternative_to_other: this.alternativeToOther,
          loanable_id: this.loanableId,
          borrower_user_id: this.$store.state.user.id,
          platform_tip: this.loan.platform_tip,
        },
        {
          notifications: {
            action: this.$t("components.loan.new_loan_form.notifications.request.action"),
          },
        }
      );

      this.$emit("input", data);
    },
    disabledBeforeToday(date) {
      return dayjs().isAfter(dayjs(date).tz(this.loanableTimezone, true), "day");
    },
    disabledBeforeNow(date) {
      return dayjs().isAfter(dayjs(date).tz(this.loanableTimezone, true), "minutes");
    },
    disabledBeforeDeparture(date) {
      return dayjs(date).tz(this.loanableTimezone, true).isSameOrBefore(this.departureAt, "minute");
    },
    disabledBeforeDepartureDay(date) {
      return dayjs(date).tz(this.loanableTimezone, true).isBefore(this.departureAt, "day");
    },
    commitSearchDate(date) {
      this.$store.commit(
        "loanable.search/searchDepartureAt",
        dayjs.atTz(date, this.loanableTimezone).format("YYYY-MM-DD HH:mm")
      );
    },
    commitSearchDuration(duration) {
      this.$store.commit("loanable.search/searchDuration", duration);
    },
    setDepartureAt(date) {
      this.commitSearchDate(date);

      this.$emit("estimate", {
        departure_at: date,
      });
    },
    setDuration(newDuration) {
      this.commitSearchDuration(newDuration);
      this.$emit("estimate", {
        duration_in_minutes: newDuration,
      });
    },
    setDepartureToEarliest() {
      this.setDepartureAt(getEarliestDeparture().toISOString());
    },
    changeSearchDate(datetime) {
      if (!this.invalidDuration) {
        this.setDepartureAt(datetime);
        return;
      }
      // If duration is negative, we allow user to move each date independently
      let newDuration = dayjs(this.returnAt).diff(datetime, "minute");
      this.commitSearchDuration(newDuration);
      this.commitSearchDate(datetime);

      this.$emit("estimate", {
        duration_in_minutes: newDuration,
        depature_at: datetime,
      });
    },
    setReturnAt(returnAt) {
      let newDuration = dayjs(returnAt).diff(this.departureAt, "minute");
      this.setDuration(newDuration);
    },
    estimateLoan: flexibleDebounce(function () {
      this.$emit(
        "estimate",
        {
          estimated_distance: this.localEstimatedDistance,
        },
        600
      );
    }),
  },
};
</script>

<style lang="scss"></style>
